import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

import {
  SprengnetterValuationHubValuationPackageModelResponseAvmPriceResponse as AvmPriceResponse,
  SprengnetterValuationHubValuationPackageModelResponseDemandScoreAvmResponse as DemandScoreResponse,
  GetServiceApiValuationReportRealEstateContactApiResponse,
  SprengnetterValuationHubProsaWebModelResponseProsaWebResponse as ProsaWebResponse,
  SprengnetterValuationHubValuationReportModelResponseSonePriceResponse as SonePriceResponse,
  ValuationHubCoreModelDtoValuationReportConfigurationDto,
  ValuationHubCoreModelImage,
  ValuationHubCoreModelRealEstateAddress,
  generatedApi,
} from "@/api/generatedApi";
import { PropertyInformation } from "@/types/refineRealEstate";

export interface CurrentValuationState {
  id: string | null;
  address: ValuationHubCoreModelRealEstateAddress | null;
  propertyInformation: PropertyInformation | null;
  reportAskingPrice?: number | null;
  images: ValuationHubCoreModelImage[] | null;
  avm: AvmPriceResponse | null;
  sone: SonePriceResponse | null;
  prosaWeb: ProsaWebResponse | null;
  demandScore: DemandScoreResponse | null;
  contact: GetServiceApiValuationReportRealEstateContactApiResponse | null;
  report: {
    reportFileName?: string | null;
    reportTimestamp?: string | null;
    reportAskingPrice?: number | null;
  };
  reportConfigurationDraft: ValuationHubCoreModelDtoValuationReportConfigurationDto | null;
  expired?: boolean;
  isValuating: boolean;
}

const initialState: CurrentValuationState = {
  id: null,
  address: null,
  propertyInformation: null,
  reportAskingPrice: null,
  images: null,
  avm: null,
  sone: null,
  prosaWeb: null,
  demandScore: null,
  contact: null,
  report: {
    reportFileName: null,
    reportTimestamp: null,
    reportAskingPrice: null,
  },
  reportConfigurationDraft: null,
  isValuating: false,
};

export const currentValuationSlice = createSlice({
  name: "currentValuation",
  initialState,
  reducers: {
    clearCurrentValuation: () => {
      return initialState;
    },
    setRealEstateData: (state, action: PayloadAction<Partial<CurrentValuationState>>) => {
      return {
        ...state,
        id: action.payload.id ?? state.id,
        address: action.payload.address ?? state.address,
        propertyInformation: action.payload.propertyInformation ?? state.propertyInformation,
      };
    },
    setImages: (state, action: PayloadAction<ValuationHubCoreModelImage[] | null>) => {
      state.images = action.payload;
    },
    setContact: (state, action: PayloadAction<GetServiceApiValuationReportRealEstateContactApiResponse | null>) => {
      state.contact = action.payload;
    },
    updatePropertyDetails: (state, action: PayloadAction<Partial<PropertyInformation>>) => {
      if (!state.propertyInformation) {
        state.propertyInformation = {
          generalInfo: {},
          plotData: {},
          buildingData: {},
          energyAndPhotovoltaic: {},
          buildingStandard: {},
          modernization: {},
          financialAndRentalInfo: {},
        } as PropertyInformation;
      }

      state.propertyInformation = {
        ...state.propertyInformation,
        generalInfo: action.payload.generalInfo
          ? {
              ...(state.propertyInformation.generalInfo || {}),
              ...action.payload.generalInfo,
            }
          : state.propertyInformation.generalInfo,
        plotData: action.payload.plotData
          ? {
              ...(state.propertyInformation.plotData || {}),
              ...action.payload.plotData,
            }
          : state.propertyInformation.plotData,
        buildingData: action.payload.buildingData
          ? {
              ...(state.propertyInformation.buildingData || {}),
              ...action.payload.buildingData,
            }
          : state.propertyInformation.buildingData,
        energyAndPhotovoltaic: action.payload.energyAndPhotovoltaic
          ? {
              ...(state.propertyInformation.energyAndPhotovoltaic || {}),
              ...action.payload.energyAndPhotovoltaic,
            }
          : state.propertyInformation.energyAndPhotovoltaic,
        buildingStandard: action.payload.buildingStandard
          ? {
              ...(state.propertyInformation.buildingStandard || {}),
              ...action.payload.buildingStandard,
            }
          : state.propertyInformation.buildingStandard,
        modernization: action.payload.modernization
          ? {
              ...(state.propertyInformation.modernization || {}),
              ...action.payload.modernization,
            }
          : state.propertyInformation.modernization,
        financialAndRentalInfo: action.payload.financialAndRentalInfo
          ? {
              ...(state.propertyInformation.financialAndRentalInfo || {}),
              ...action.payload.financialAndRentalInfo,
            }
          : state.propertyInformation.financialAndRentalInfo,
        propertyImages: action.payload.propertyImages
          ? {
              ...(state.propertyInformation.propertyImages || {}),
              ...action.payload.propertyImages,
            }
          : state.propertyInformation.propertyImages,
      };
    },
    updatePrice: (state, action: PayloadAction<number | null>) => {
      state.reportAskingPrice = action.payload;
    },

    updateSone: (state, action: PayloadAction<SonePriceResponse | null>) => {
      state.sone = action.payload;
    },

    updateAvm: (state, action: PayloadAction<AvmPriceResponse | null>) => {
      state.avm = action.payload;
    },

    updateProsaWeb: (state, action: PayloadAction<ProsaWebResponse | null>) => {
      state.prosaWeb = action.payload;
    },

    updateDemandScore: (state, action: PayloadAction<DemandScoreResponse | null>) => {
      state.demandScore = action.payload;
    },

    updateReport: (state, action: PayloadAction<Partial<CurrentValuationState["report"]>>) => {
      state.report = {
        ...(state.report || {}),
        ...action.payload,
      };
    },

    updateReportConfigurationDraft: (
      state,
      action: PayloadAction<ValuationHubCoreModelDtoValuationReportConfigurationDto | null>,
    ) => {
      state.reportConfigurationDraft = action.payload;
    },

    updateExpired: (state, action: PayloadAction<boolean>) => {
      state.expired = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        generatedApi.endpoints.postServiceApiValuationPackageValuationByItemIdValuate.matchPending,
        (state) => {
          state.isValuating = true;
        },
      )
      .addMatcher(
        generatedApi.endpoints.postServiceApiValuationPackageValuationByItemIdValuate.matchFulfilled,
        (state) => {
          state.isValuating = false;
        },
      )
      .addMatcher(
        generatedApi.endpoints.postServiceApiValuationPackageValuationByItemIdValuate.matchRejected,
        (state) => {
          state.isValuating = false;
        },
      );
  },
});

export const {
  clearCurrentValuation,
  setRealEstateData,
  setImages,
  setContact,
  updatePropertyDetails,
  updateSone,
  updateAvm,
  updateProsaWeb,
  updateDemandScore,
  updateReport,
  updateReportConfigurationDraft,
  updatePrice,
  updateExpired,
} = currentValuationSlice.actions;

export const CurrentValuationReducer = currentValuationSlice.reducer;
